import type { QueryKey, UseQueryOptions, UseQueryReturnType } from '@tanstack/vue-query';
import { useQuery } from '@tanstack/vue-query';
import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from 'vue';
import { useSessionStore } from '@/stores/useSessionStore';
import { isElectron } from '@/utils/clientUtils';
import { getAxiosInstance } from '@/api/axios';

type AuthenticatedQueryParams<TQueryFnData, TError, TData> = UseQueryOptions<TQueryFnData, TError, TData> & { queryKey: QueryKey };

const ONE_SECOND_IN_MS = 1_000;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;

export const useAuthenticatedQuery = <TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
  params: AuthenticatedQueryParams<TQueryFnData, TError, TData> & { enabled?: MaybeRefOrGetter<boolean> }
): UseQueryReturnType<TData, TError> => {
  const $session = useSessionStore();

  // Reactive enabled condition combining session state and provided enabled condition
  const isEnabled: ComputedRef<boolean> = computed(() => {
    const isAuthenticated = $session._session.isAuthenticated;
    // If enabled is not defined in queryConfig, assume true
    const isExplicitlyDisabled = toValue(params.enabled) === false;
    return isAuthenticated && !!getAxiosInstance() && !isExplicitlyDisabled;
  });
  // Override the enabled property with the reactive condition
  const modifiedQueryConfig = { ...params, enabled: isEnabled, refetchIntervalInBackground: isElectron() };
  return useQuery<TQueryFnData, TError, TData>(modifiedQueryConfig);
};
